/**
 * Single-writer lease across tabs.
 *
 * Only the leader runs recovery and the outbox, so two open tabs do not both
 * fire the same post-confirmation effect.
 *
 * IMPORTANT — this is an optimisation, not the correctness boundary.
 * `localStorage` has no compare-and-swap, so two tabs starting simultaneously
 * can briefly both believe they hold the lease. Exactly-once is guaranteed by
 * the deterministic `effectKey` plus the server-side unique index; the lease
 * just keeps duplicate work (and duplicate rejected-as-duplicate round trips)
 * rare in normal operation.
 */

import { LEADER_KEY } from "./keys";
import * as storage from "./storage";
import { parse, stringify } from "./codec";

const HEARTBEAT_MS = 1000;
const LEASE_TTL_MS = 3000;

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let timer = null;
let holding = false;
let onChange = null;

function readLease() {
  return parse(storage.getItem(LEADER_KEY), null);
}

function writeLease() {
  storage.setItem(LEADER_KEY, stringify({ holder: tabId, ts: Date.now() }));
}

function evaluate() {
  const lease = readLease();
  const now = Date.now();
  const expired = !lease || typeof lease.ts !== "number" || now - lease.ts > LEASE_TTL_MS;

  const wasHolding = holding;

  if (!lease || expired) {
    writeLease();
    holding = true;
  } else if (lease.holder === tabId) {
    writeLease(); // renew
    holding = true;
  } else {
    holding = false;
  }

  if (holding !== wasHolding && typeof onChange === "function") onChange(holding);
}

export function start(changeHandler) {
  onChange = changeHandler;
  if (timer) return;
  evaluate();
  timer = setInterval(evaluate, HEARTBEAT_MS);

  // Hand the lease over promptly on navigate-away so the next tab does not have
  // to wait out the full TTL.
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", release);
  }
}

export function stop() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (typeof window !== "undefined") {
    window.removeEventListener("pagehide", release);
  }
  release();
  onChange = null;
}

export function release() {
  if (!holding) return;
  const lease = readLease();
  if (lease && lease.holder === tabId) storage.removeItem(LEADER_KEY);
  holding = false;
}

export function isLeader() {
  return holding;
}

export const __testing = {
  tabId,
  HEARTBEAT_MS,
  LEASE_TTL_MS,
  forceEvaluate: evaluate,
  reset() {
    if (timer) clearInterval(timer);
    timer = null;
    holding = false;
    onChange = null;
    storage.removeItem(LEADER_KEY);
  },
};
